import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';
import { clearLegacyPlatformTokensFromStorage } from '../utils/legacyPlatformTokens';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is logged in on app start
  useEffect(() => {
    clearLegacyPlatformTokensFromStorage();
    const token = localStorage.getItem('access_token');
    if (token) {
      authAPI
        .getCurrentUser()
        .then((userData) => {
          setUser(userData);
        })
        .catch((err) => {
          console.error('Failed to get current user:', err);
          localStorage.removeItem('access_token');
          localStorage.removeItem('user');
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    try {
      setError(null);
      const response = await authAPI.login(email, password);

      // Store token and user data
      localStorage.setItem('access_token', response.access_token);
      localStorage.setItem(
        'user',
        JSON.stringify({
          id: response.user_id,
          email: response.email,
          username: response.username,
        })
      );

      setUser({
        id: response.user_id,
        email: response.email,
        username: response.username,
      });

      return response;
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
      throw err;
    }
  };

  const register = async (userData) => {
    try {
      setError(null);
      const response = await authAPI.register(userData);

      // Store token and user data
      localStorage.setItem('access_token', response.access_token);
      localStorage.setItem(
        'user',
        JSON.stringify({
          id: response.user_id,
          email: response.email,
          username: response.username,
        })
      );

      setUser({
        id: response.user_id,
        email: response.email,
        username: response.username,
      });

      return response;
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed');
      throw err;
    }
  };

  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
    setUser(null);
    setError(null);
  };

  const clearError = () => {
    setError(null);
  };

  const updateProfile = async (profileData) => {
    try {
      // TODO: API call to update profile
      // For now, just update local state
      const updatedUser = { ...user, ...profileData };
      setUser(updatedUser);

      // Update localStorage
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      const updatedStoredUser = { ...storedUser, ...profileData };
      localStorage.setItem('user', JSON.stringify(updatedStoredUser));

      return updatedUser;
    } catch (err) {
      setError(err.response?.data?.detail || 'Profile update failed');
      throw err;
    }
  };

  const value = {
    user,
    loading,
    error,
    login,
    register,
    logout,
    clearError,
    updateProfile,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
